import { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Bot,
  History,
  Send,
  ArrowLeft,
  User,
  File,
  FileText,
  BarChart2,
  Files,
} from "lucide-react";
import {
  useGetEssayGraders,
  useSendMessageToEssayGrader,
  getGetEssayGraderQueryKey,
  useSendMessageToSingleEssayGrader,
} from "@packages/api/madlenAPI";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  EssayGradingChatMessageResponseDto,
  EssayGradingResultDto,
  TeacherDocumentResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { useQueryClient } from "@tanstack/react-query";
import { assistantColorSchemes } from "@packages/utils";
import Markdown from "react-markdown";
import { useAppSelector } from "@packages/store/hooks";
import { useGetEssayGrader } from "@packages/api/madlenAPI";
import { trackEvent } from "@packages/hooks/useAnalytics";
import Modal from "@core/modal";
import { Text, Button } from "@core/index";
import remarkGfm from "remark-gfm";
import { ChatMessageBox } from "./essay-grader/ChatMessageBox";
import { HistoryModal } from "./essay-grader/HistoryModal";
const FilePreview = ({
  fileName,
  fileUrl,
}: {
  fileName: string;
  fileUrl: string;
}) => {
  return (
    <Link to={fileUrl} target="_blank" rel="noopener noreferrer" className="">
      <div className="flex items-center pt-4 transition-colors cursor-pointer group">
        <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
          <FileText className="w-6 h-6 group-hover:text-indigo-600 group-hover:dark:text-indigo-400" />
        </div>
        <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate pl-2">
          {fileName}
        </h3>
      </div>
    </Link>
  );
};

const ChatBubble = ({
  message,
  avatar,
}: {
  message: EssayGradingChatMessageResponseDto;
  avatar: string | null;
}) => {
  const isUser = message.sender === "USER";
  const { i18n } = useTranslation();

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      <div
        className={`flex ${isUser ? "flex-row-reverse" : "flex-row"} items-end max-w-[80%]`}
      >
        <div
          className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center 
          ${isUser ? "ml-2 bg-indigo-100 dark:bg-indigo-900/50" : "mr-2 bg-gray-100 dark:bg-gray-800"}`}
        >
          {isUser ? (
            avatar ? (
              <img
                src={avatar}
                alt="User"
                className="w-8 h-8 rounded-full"
                referrerPolicy="no-referrer"
              />
            ) : (
              <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            )
          ) : (
            <Bot className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          )}
        </div>

        <div
          className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
        >
          <div
            className={`px-4 py-2 rounded-2xl break-words
            ${
              isUser
                ? "bg-indigo-600 text-white dark:bg-indigo-500 rounded-br-none"
                : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-none border border-gray-200 dark:border-gray-700"
            }`}
          >
            <Markdown
              className={`prose dark:prose-invert max-w-none ${
                isUser
                  ? "text-white prose-headings:text-white prose-strong:text-white prose-em:text-white prose-table:text-white"
                  : "prose-table:border-gray-200 dark:prose-table:border-gray-700"
              } prose-table:border prose-td:border prose-td:border-gray-200 dark:prose-td:border-gray-700 prose-th:border prose-th:border-gray-200 dark:prose-th:border-gray-700 prose-th:bg-gray-50 dark:prose-th:bg-gray-900 prose-td:p-2 prose-th:p-2 prose-pre:whitespace-pre-wrap prose-pre:break-words  prose-pre:overflow-auto prose-pre:max-w-full`}
              remarkPlugins={[remarkGfm]}
            >
              {message.message}
            </Markdown>
          </div>
          <div className={`text-xs text-gray-500 dark:text-gray-400 mt-1`}>
            {message.createdAt
              ? new Date(message.createdAt).toLocaleTimeString(i18n.language, {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: i18n.language === "tr" ? false : true,
                })
              : ""}
          </div>
        </div>
      </div>
    </div>
  );
};

const FileViewerPopover = ({
  isOpen,
  onClose,
  files,
  rubricFile,
}: {
  isOpen: boolean;
  onClose: () => void;
  files: TeacherDocumentResponseDto[] | undefined;
  rubricFile?: TeacherDocumentResponseDto;
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-50 transition-opacity duration-200"
        onClick={onClose}
      />
      <div className="absolute right-0 top-0 mt-2 w-[480px] rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200/50 dark:border-gray-700/50 z-50 transform transition-all duration-200 ease-out animate-slide-up">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center space-x-2">
              <Files className="w-5 h-5 text-violet-500 dark:text-violet-400" />
              <Text variant="h3" className="text-gray-900 dark:text-white">
                {t("assistant.files")}
              </Text>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
            {files?.length === 0 && !rubricFile ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-600" />
                <Text variant="p" status="secondary" className="text-sm">
                  No files attached
                </Text>
              </div>
            ) : (
              <>
                {files && files.length > 0 && (
                  <div>
                    <Text
                      variant="p"
                      status="secondary"
                      className="text-xs uppercase tracking-wider"
                    >
                      Essay Files
                    </Text>
                    {files.map((file) => (
                      <FilePreview
                        key={file.id}
                        fileName={file.fileName || ""}
                        fileUrl={file.downloadUrl || ""}
                      />
                    ))}
                  </div>
                )}
                {rubricFile && (
                  <div>
                    <Text
                      variant="p"
                      status="secondary"
                      className="text-xs uppercase tracking-wider"
                    >
                      Rubric
                    </Text>
                    <FilePreview
                      key={rubricFile.id}
                      fileName={rubricFile.fileName || ""}
                      fileUrl={rubricFile.downloadUrl || ""}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const SingleAssistantEssayGrader = ({
  isSidebarCollapsed,
}: {
  isSidebarCollapsed: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const queryClient = useQueryClient();
  const avatar = useAppSelector((state) => state.user.avatar);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showEssayGraderModal, setShowEssayGraderModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [searchParams] = useSearchParams();
  const subId = searchParams.get("subId");
  const isEssayGrader = searchParams.get("isEssayGrader") === "true";
  const teacherPrefencedLanguage = useAppSelector(
    (state) => state.user.teacher?.preferences?.language,
  );
  const [showFilesPopover, setShowFilesPopover] = useState(false);

  const { data: customGpt } = useGetEssayGrader(Number(id), {
    query: {
      refetchInterval: (data) => {
        if (
          data?.state.data?.messages?.length === 0 ||
          data?.state.data?.messages?.[data?.state.data?.messages.length - 1]
            ?.sender === "USER"
        ) {
          return 2000;
        }
        return false;
      },
    },
  });

  const { data: oldChatsRaw, isLoading: isLoadingOldChats } =
    useGetEssayGraders({
      size: 100,
    });

  const oldChats = useMemo(() => {
    return oldChatsRaw?.data?.sort((a, b) => {
      return (
        new Date(b.createdAt || "").getTime() -
        new Date(a.createdAt || "").getTime()
      );
    });
  }, [oldChatsRaw]);

  const isWaitingForResponse = useMemo(() => {
    if (subId) {
      const subResult = customGpt?.results?.find(
        (result) => result.id === Number(subId),
      );
      return (
        subResult?.messages?.length &&
        subResult?.messages[subResult?.messages.length - 1].sender === "USER"
      );
    }
    return customGpt?.messages?.length &&
      customGpt?.messages[customGpt?.messages.length - 1].sender === "USER"
      ? true
      : false;
  }, [customGpt, subId]);

  const { mutate: sendMessageToSingleEssayGrader } =
    useSendMessageToSingleEssayGrader({
      mutation: {
        onMutate: (variables) => {
          const previousData = queryClient.getQueryData(
            getGetEssayGraderQueryKey(Number(id)),
          );
          queryClient.setQueryData(
            getGetEssayGraderQueryKey(Number(id)),
            (oldData: EssayGradingResultDto) => {
              return {
                ...oldData,
                messages: [
                  ...(oldData.messages || []),
                  {
                    message: variables.data.message,
                    sender: variables.data.sender,
                    id: Date.now(),
                    createdAt: new Date().toISOString(),
                  },
                ],
              };
            },
          );
          return { previousData };
        },
        onError: (error, _variables, context) => {
          queryClient.setQueryData(
            getGetEssayGraderQueryKey(Number(id)),
            context?.previousData,
          );
          console.error("Error sending message:", error);
        },
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: getGetEssayGraderQueryKey(Number(id)),
          });
        },
      },
    });

  const { mutate: sendMessageToEssayGrader } = useSendMessageToEssayGrader({
    mutation: {
      onMutate: (variables) => {
        const previousData = queryClient.getQueryData(
          getGetEssayGraderQueryKey(Number(id)),
        );
        queryClient.setQueryData(
          getGetEssayGraderQueryKey(Number(id)),
          (oldData: EssayGradingResultDto) => {
            return {
              ...oldData,
              messages: [
                ...(oldData.messages || []),
                {
                  message: variables.data.message,
                  sender: variables.data.sender,
                  id: Date.now(),
                  createdAt: new Date().toISOString(),
                },
              ],
            };
          },
        );
        return { previousData };
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(
          getGetEssayGraderQueryKey(Number(id)),
          context?.previousData,
        );
        console.error("Error sending message:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEssayGraderQueryKey(Number(id)),
        });
      },
    },
  });

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const newMessage = message;
    setMessage("");

    if (subId) {
      sendMessageToSingleEssayGrader(
        {
          data: { message: newMessage, sender: "USER" },
          mainId: Number(id),
          singleId: Number(subId),
        },
        {},
      );
    } else {
      sendMessageToEssayGrader(
        { data: { message: newMessage, sender: "USER" }, id: Number(id) },
        {},
      );
    }

    track(
      {
        category: "ContentSendMessage",
        action: "essay.grader.send.message",
        label: "EssayGrader send message",
        nonInteraction: false,
      },
      {
        location: "/essay-grader/send-message",
        element: "send-button",
        essayGraderMessage: newMessage,
      },
    );
  };

  const { trackEvent: track } = trackEvent();
  const handleClickHistory = () => {
    track(
      {
        category: "ContentHistory",
        action: "essay.grader.history",
        label: "EssayGrader history click",
        nonInteraction: false,
      },
      {
        location: "/essay-grader/history",
        element: "history-button",
      },
    );
    setShowHistory(true);
  };

  const handleSelectChat = (chatId: number, subId?: number) => {
    setShowHistory(false);
    console.log(subId);
    if (subId) {
      navigate(
        `/assistants/new/ESSAY_GRADER/${chatId}?isEssayGrader=true&subId=${subId}`,
      );
    } else {
      navigate(`/assistants/new/ESSAY_GRADER/${chatId}?isEssayGrader=true`);
    }
  };

  // const handleClickNew = () => {
  //   track(
  //     {
  //       category: "ContentNew",
  //       action: "essay.grader.new",
  //       label: "EssayGrader new click",
  //       nonInteraction: false,
  //     },
  //     {
  //       location: "/essay-grader/new",
  //       element: "new-button",
  //     },
  //   );
  //   setShowEssayGraderModal(true);
  // };

  const assistantColorScheme = useMemo(() => {
    return assistantColorSchemes["ESSAY_GRADER"];
  }, []);

  // const onDrop = useCallback(
  //   async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
  //     if (rejectedFiles.length > 0) {
  //       addToast({
  //         title: t("assistant.unsupportedFile"),
  //         description: t("assistant.supportedFilesDescription"),
  //         variant: "warning",
  //         duration: 3000,
  //       });
  //       return;
  //     }

  //     const file = acceptedFiles[0];
  //     setSelectedFile(file);
  //     setIsUploading(true);
  //     setUploadProgress(0);

  //     try {
  //       const response = await generatePresignedUrl({
  //         fileName: file.name,
  //       });

  //       return new Promise((resolve, reject) => {
  //         const xhr = new XMLHttpRequest();
  //         xhr.open("PUT", response.url!, true);

  //         xhr.upload.onprogress = (e) => {
  //           const progress = (e.loaded / e.total) * 90;
  //           setUploadProgress(Math.round(progress));
  //         };

  //         xhr.onload = () => {
  //           if (xhr.status === 200) {
  //             const s3Url = response.url?.split("?")[0] || "";
  //             const decodedFilename = decodeURIComponent(s3Url);
  //             createCustomGptFromS3ToAzureVectorStore(Number(id), {
  //               name: file.name || "",
  //               s3Url: decodedFilename,
  //             }).then(() => {
  //               addToast({
  //                 title: t("assistant.fileUploaded"),
  //                 description: t("assistant.fileUploadedDescription"),
  //                 variant: "success",
  //                 duration: 3000,
  //               });
  //               queryClient.invalidateQueries({
  //                 queryKey: getGetCustomGptQueryKey(Number(id)),
  //               });
  //               setIsUploading(false);
  //               setSelectedFile(null);
  //               setUploadProgress(0);
  //               resolve(xhr.response);
  //             });
  //           } else {
  //             reject(new Error("Upload failed"));
  //           }
  //         };

  //         xhr.onerror = () => reject(new Error("Upload failed"));
  //         xhr.send(file);
  //       });
  //     } catch (error) {
  //       console.error("Error uploading file:", error);
  //       addToast({
  //         title: t("assistant.uploadError"),
  //         description: t("assistant.uploadErrorDescription"),
  //         variant: "error",
  //         duration: 3000,
  //       });
  //     }
  //   },
  //   [],
  // );

  // const { getRootProps, getInputProps, isDragActive } = useDropzone({
  //   onDrop,
  //   accept: {
  //     "application/pdf": [".pdf"],
  //     "application/msword": [".doc"],
  //     "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
  //       [".docx"],
  //     "text/plain": [".txt"],
  //     "application/vnd.openxmlformats-officedocument.presentationml.presentation":
  //       [".pptx"],
  //   },
  //   maxSize: 15 * 1024 * 1024, // 15MB
  //   noClick: true,
  //   noDragEventsBubbling: true,
  // });

  // const {
  //   getRootProps: getButtonRootProps,
  //   getInputProps: getButtonInputProps,
  // } = useDropzone({
  //   onDrop,
  //   accept: {
  //     "application/pdf": [".pdf"],
  //     "application/msword": [".doc"],
  //     "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
  //       [".docx"],
  //     "text/plain": [".txt"],
  //     "application/vnd.openxmlformats-officedocument.presentationml.presentation":
  //       [".pptx"],
  //   },
  //   maxSize: 15 * 1024 * 1024, // 15MB
  //   noDragEventsBubbling: true,
  // });

  useEffect(() => {
    if (!isWaitingForResponse && !isUploading) {
      textareaRef.current?.focus();
    }
  }, [isWaitingForResponse, isUploading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Add useEffect to scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [customGpt?.messages, customGpt?.results]);

  const autoResizeTextarea = (element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };

  // Initialize auto-resize and handle message changes
  useEffect(() => {
    if (textareaRef.current) {
      console.log("textareaRef.current", textareaRef.current);
      autoResizeTextarea(textareaRef.current);
    }
  }, [message]);

  const handleOpenAnalysis = () => {
    track(
      {
        category: "ContentAnalysis",
        action: "essay.grader.analysis",
        label: "EssayGrader analysis click",
        nonInteraction: false,
      },
      {
        location: "/essay-grader/analysis",
        element: "analysis-button",
      },
    );
    if (
      customGpt &&
      customGpt?.results &&
      customGpt?.results[0]?.result?.lang_code
    ) {
      const language = customGpt?.results[0]?.result?.lang_code;

      if (language === "en" || language === "en-US") {
        i18n.changeLanguage("en");
      } else if (language === "tr" || language === "tr-TR") {
        i18n.changeLanguage("tr");
      }
    }

    setShowAnalysisModal(true);
  };

  // Handle back navigation
  const handleBackNavigation = () => {
    if (isEssayGrader && subId && id) {
      // Navigate back to essay grader details view with the specific essay selected
      navigate(`/essay-grading?chatId=${id}&subId=${subId}&view=details`);
    } else {
      // Default navigation to homepage
      navigate("/");
    }
  };

  if (!assistantColorScheme || !customGpt) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="relative">
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 via-indigo-500/20 to-violet-500/20 animate-gradient-x rounded-2xl blur-xl" />

          {/* Content container */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center gap-6">
              {/* Animated logo/spinner */}
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-violet-500 animate-spin" />
                <Bot className="w-6 h-6 text-violet-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>

              {/* Text content */}
              <div className="text-center space-y-2">
                <Text variant="h2">assistant.loading</Text>
                <Text variant="p" status="secondary">
                  assistant.waitingForResponse
                </Text>
              </div>

              {/* Animated dots */}
              <div className="flex space-x-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-2 bg-violet-500 rounded-full animate-pulse"
                    style={{ animationDelay: `${i * 200}ms` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      // {...getRootProps()}
      className={`flex flex-col ${isSidebarCollapsed ? "h-[calc(100vh-80px)]" : "h-[100vh]"} bg-gray-50 dark:bg-gray-900 relative`}
    >
      {/* <input {...getInputProps()} /> */}

      {/* Header - Fixed height */}
      <div className="flex-none border-b dark:border-gray-800 bg-white dark:bg-gray-800 p-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between sm:items-center">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleBackNavigation}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <assistantColorScheme.icon
              className={`w-6 h-6 ${assistantColorScheme.iconColor}`}
            />
            <Text variant={"h2"}>
              {`assistant.assistants.ESSAY_GRADER.title`}
            </Text>
          </div>
          <div className="flex items-center justify-end space-x-2 relative mt-2 sm:mt-0">
            <Button
              onClick={() => setShowFilesPopover(!showFilesPopover)}
              rounded="lg"
              bgColor="bg-violet-50 hover:bg-violet-100 dark:bg-violet-900/30 dark:hover:bg-violet-900/50"
              textColor="text-violet-600 dark:text-violet-400"
              size="md"
              className="border border-violet-200 dark:border-violet-800"
            >
              <Files className="w-5 h-5" />
              <span className="hidden sm:block">
                {t("assistant.viewFiles")}
              </span>
            </Button>
            {customGpt?.results?.[0]?.result?.overall_summary && (
              <Button
                onClick={handleOpenAnalysis}
                rounded="lg"
                bgColor="bg-violet-50 hover:bg-violet-100 dark:bg-violet-900/30 dark:hover:bg-violet-900/50"
                textColor="text-violet-600 dark:text-violet-400"
                size="md"
                className="border border-violet-200 dark:border-violet-800"
              >
                <BarChart2 className="w-5 h-5" />
                <span className="hidden sm:block">
                  {t("assistant.essayGrader.viewAnalysis")}
                </span>
              </Button>
            )}
            <Button
              onClick={handleClickHistory}
              rounded="lg"
              bgColor="hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600"
              textColor="dark:text-gray-300 "
              size="md"
            >
              <History className="w-5 h-5" />
              <span>{t("assistant.viewHistory")}</span>
            </Button>
            <FileViewerPopover
              isOpen={showFilesPopover}
              onClose={() => setShowFilesPopover(false)}
              files={
                subId
                  ? customGpt?.results
                      ?.filter((result) => result.id === Number(subId))
                      ?.flatMap((result) => result.essayFiles)
                      ?.filter(Boolean)
                  : customGpt?.essayFiles || []
              }
              rubricFile={customGpt?.rubricFile}
            />
          </div>
        </div>
      </div>

      {/* Chat Area - Flexible height */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-12">
        <div className="max-w-7xl mx-auto space-y-6">
          {subId
            ? customGpt?.results
                ?.find((result) => result.id === Number(subId))
                ?.messages?.map((message) => (
                  <ChatBubble
                    key={message.id}
                    message={message}
                    avatar={avatar}
                  />
                ))
            : customGpt?.messages?.map((message) => (
                <ChatBubble
                  key={message.id}
                  message={message}
                  avatar={avatar}
                />
              ))}
          {isWaitingForResponse && (
            <div className="flex justify-start mb-4">
              <div className="flex flex-row items-end space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-violet-500/20 to-violet-500/10 dark:from-violet-400/20 dark:to-violet-400/10 backdrop-blur-sm">
                    <Bot className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-violet-500 dark:bg-violet-400 rounded-full animate-pulse" />
                </div>
                <div className="px-6 py-3 rounded-2xl rounded-bl-none bg-gradient-to-br from-white to-violet-50 dark:from-gray-800 dark:to-violet-900/20 border border-violet-200 dark:border-violet-800/50 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-2 h-2 bg-violet-500/60 dark:bg-violet-400/60 rounded-full animate-pulse"
                      style={{ animationDelay: "0ms", animationDuration: "1s" }}
                    />
                    <div
                      className="w-2 h-2 bg-violet-500/60 dark:bg-violet-400/60 rounded-full animate-pulse"
                      style={{
                        animationDelay: "300ms",
                        animationDuration: "1s",
                      }}
                    />
                    <div
                      className="w-2 h-2 bg-violet-500/60 dark:bg-violet-400/60 rounded-full animate-pulse"
                      style={{
                        animationDelay: "600ms",
                        animationDuration: "1s",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Fixed height */}
      <div className="flex-none border-t dark:border-gray-800 bg-white dark:bg-gray-800 p-4">
        <div className="max-w-7xl mx-auto">
          {selectedFile && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <File className="w-5 h-5 text-gray-500" />
                  <Text variant={"span"} size="sm" status="secondary">
                    {selectedFile.name}
                  </Text>
                </div>
                {isUploading && (
                  <div className="flex items-center space-x-2">
                    <div className="h-1 w-24 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-violet-500 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <Text variant={"span"} size="sm" status="secondary">
                      {uploadProgress}%
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative flex items-center my-2">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  isWaitingForResponse
                    ? t("assistant.waitingForResponse")
                    : t("assistant.typePlaceholder")
                }
                className={`w-full px-4 py-3 pr-12 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-600 focus:border-transparent transition-opacity duration-200 min-h-14 max-h-96 resize-none overflow-y-auto   ${
                  isWaitingForResponse || isUploading
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                disabled={isWaitingForResponse || isUploading}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    !e.shiftKey &&
                    !isWaitingForResponse &&
                    !isUploading
                  ) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              {/* <div {...getRootProps()}>
                <input {...getInputProps()} />
                <div {...getButtonRootProps()}>
                  <input {...getButtonInputProps()} />
                  <Button
                    type="button"
                    className={`absolute right-3 top-[50%] -translate-y-[50%] text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 ${
                      isWaitingForResponse || isUploading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    disabled={isWaitingForResponse || isUploading}
                  >
                    <File className="w-5 h-5" />
                  </Button>
                </div>
              </div> */}
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={
                (!message.trim() && !selectedFile) ||
                isWaitingForResponse ||
                isUploading
              }
              bgColor="bg-violet-600 hover:bg-violet-700 dark:bg-violet-700 dark:hover:bg-violet-800"
              textColor="text-white"
              rounded="lg"
              size="md"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>

          {/* {isDragActive && (
            <div className="absolute inset-0 bg-violet-500/10 dark:bg-violet-400/10 backdrop-blur-sm flex items-center justify-center">
              <div className="p-6 rounded-lg bg-white dark:bg-gray-800 shadow-lg border-2 border-dashed border-violet-500 dark:border-violet-400">
                <div className="text-center">
                  <File className="w-8 h-8 mx-auto mb-2 text-violet-500 dark:text-violet-400" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Drop your file here
                  </p>
                </div>
              </div>
            </div>
          )} */}
        </div>
      </div>

      {/* Analysis Modal */}
      <Modal
        show={showAnalysisModal}
        onClose={() => {
          setShowAnalysisModal(false);
          i18n.changeLanguage(teacherPrefencedLanguage);
        }}
        outerClassName="max-w-6xl"
      >
        {subId ? (
          <ChatMessageBox
            message={
              customGpt?.results?.find((result) => result.id === Number(subId))
                ?.result
            }
            t={t}
            onNavigate={() => {
              setShowAnalysisModal(false);
              i18n.changeLanguage(teacherPrefencedLanguage);
            }}
          />
        ) : (
          customGpt?.results?.[0]?.result?.overall_summary && (
            <ChatMessageBox
              message={customGpt?.results?.[0]?.result}
              t={t}
              onNavigate={() => {
                setShowAnalysisModal(false);
                i18n.changeLanguage(teacherPrefencedLanguage);
              }}
            />
          )
        )}
      </Modal>

      <HistoryModal
        show={showHistory}
        onClose={() => setShowHistory(false)}
        chats={oldChats || []}
        isLoading={isLoadingOldChats}
        onSelectChat={handleSelectChat}
      />

      {/* Add overlay for drag and drop - appears when dragging */}
      {/* {isDragActive && (
        <div className="absolute inset-0 bg-violet-500/10 dark:bg-violet-400/10 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className="p-8 rounded-xl bg-white dark:bg-gray-800 shadow-lg border-2 border-dashed border-violet-500 dark:border-violet-400">
            <div className="text-center">
              <File className="w-12 h-12 mx-auto mb-4 text-violet-500 dark:text-violet-400" />
              <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                {t("assistant.dropFileHere")}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("assistant.supportedFiles")}
              </p>
            </div>
          </div>
        </div>
      )} */}
      {/* <EssayGraderUploadModal
        initialRubric={{
          s3Url: customGpt?.rubricFile?.url || "",
          name: customGpt?.rubricFile?.fileName || "",
          fileId: customGpt?.rubricFile?.id,
        }}
        isOpen={showEssayGraderModal}
        onClose={() => setShowEssayGraderModal(false)}
      /> */}
    </div>
  );
};

export default SingleAssistantEssayGrader;
